import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Download, AlertTriangle, CheckCircle, Clock, Search, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stepStatusColors = {
  pending: 'bg-gray-100 text-gray-600',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  skipped: 'bg-yellow-100 text-yellow-700'
};

export default function FMSTrackingReportPage() {
  const { getAuthHeader } = useAuth();
  const [trackingData, setTrackingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRow, setExpandedRow] = useState(null); // Single expanded row (accordion style)
  const [summary, setSummary] = useState({ total: 0, delayed: 0 });

  const fetchTrackingReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/reports/fms-so-wo-tracking`, { headers: getAuthHeader() });
      setTrackingData(response.data.tracking_data || []);
      setSummary({
        total: response.data.total_tracked || 0,
        delayed: response.data.delayed_count || 0
      });
    } catch (error) {
      toast.error('Failed to fetch tracking report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrackingReport();
  }, []);

  const toggleExpand = (taskId) => {
    setExpandedRow(prev => prev === taskId ? null : taskId);
  };

  const downloadCSV = () => {
    let csv = 'FMS SO/WO Tracking Report\n';
    csv += `Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm')}\n\n`;
    csv += 'Type,Number,Client/SO,Item,Flowchart,Current Step,Progress,Has Delay,Delay Details\n';
    
    trackingData.forEach(item => {
      const entity = item.entity;
      const delayedSteps = item.steps.filter(s => s.is_delayed).map(s => `${s.title}(${s.delay_hours}h)`).join('; ');
      csv += `"${entity.type}","${entity.number}","${entity.client || entity.so_number || ''}","${entity.item || ''}","${item.flowchart_name}","${item.current_step.title}","${item.completed_steps}/${item.total_steps}","${item.has_delay ? 'YES' : 'No'}","${delayedSteps}"\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fms_tracking_report_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report downloaded');
  };

  const filteredData = trackingData.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    return (
      item.entity.number?.toLowerCase().includes(searchLower) ||
      item.entity.client?.toLowerCase().includes(searchLower) ||
      item.entity.item?.toLowerCase().includes(searchLower) ||
      item.flowchart_name?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">FMS SO/WO Tracking Report</h1>
            <p className="text-muted-foreground">Track FMS flowchart progress for Sales Orders and Work Orders</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchTrackingReport} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={downloadCSV}>
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-blue-500" />
                <div>
                  <div className="text-2xl font-bold">{summary.total}</div>
                  <div className="text-sm text-muted-foreground">Total Tracked</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={summary.delayed > 0 ? 'border-red-200 bg-red-50' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className={`w-8 h-8 ${summary.delayed > 0 ? 'text-red-500' : 'text-gray-400'}`} />
                <div>
                  <div className={`text-2xl font-bold ${summary.delayed > 0 ? 'text-red-600' : ''}`}>{summary.delayed}</div>
                  <div className="text-sm text-muted-foreground">Delayed</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-500" />
                <div>
                  <div className="text-2xl font-bold">{summary.total - summary.delayed}</div>
                  <div className="text-sm text-muted-foreground">On Track</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="flex gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by SO/WO number, client, item..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Tracking Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Number</TableHead>
                  <TableHead>Client/SO</TableHead>
                  <TableHead>Flowchart</TableHead>
                  <TableHead>Current Step</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.map((item) => (
                  <React.Fragment key={item.task_id}>
                    <TableRow 
                      className={`cursor-pointer hover:bg-muted/50 ${item.has_delay ? 'bg-red-50' : ''}`}
                      onClick={() => toggleExpand(item.task_id)}
                    >
                      <TableCell>
                        {expandedRow === item.task_id ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{item.entity.type}</Badge>
                      </TableCell>
                      <TableCell className="font-semibold">{item.entity.number}</TableCell>
                      <TableCell>{item.entity.client || item.entity.so_number || '-'}</TableCell>
                      <TableCell className="text-sm">{item.flowchart_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{item.current_step.title}</span>
                          <span className="text-xs text-muted-foreground">({item.current_step.doer})</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-green-500 transition-all"
                              style={{ width: `${(item.completed_steps / item.total_steps) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm">{item.completed_steps}/{item.total_steps}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {item.has_delay ? (
                          <Badge variant="destructive" className="animate-pulse">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Delayed
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-green-50 text-green-700">
                            On Track
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                    
                    {/* Expanded Step Details */}
                    {expandedRow === item.task_id && (
                      <TableRow>
                        <TableCell colSpan={8} className="bg-muted/30 p-4">
                          <div className="space-y-2">
                            <h4 className="font-semibold text-sm mb-3">Step-by-Step Progress</h4>
                            <div className="grid grid-cols-1 gap-2">
                              {item.steps.map((step, idx) => (
                                <div 
                                  key={idx}
                                  className={`flex items-center justify-between p-3 rounded-lg border ${
                                    step.is_delayed ? 'bg-red-50 border-red-200' : 
                                    step.is_current ? 'bg-blue-50 border-blue-200' :
                                    step.status === 'completed' ? 'bg-green-50 border-green-200' :
                                    'bg-white border-gray-200'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                      step.status === 'completed' ? 'bg-green-500 text-white' :
                                      step.is_current ? 'bg-blue-500 text-white' :
                                      'bg-gray-200 text-gray-600'
                                    }`}>
                                      {step.step_number}
                                    </div>
                                    <div>
                                      <div className="font-medium">{step.title}</div>
                                      <div className="text-xs text-muted-foreground">Doer: {step.doer}</div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <div className="text-right text-sm">
                                      <div className="text-muted-foreground">Due: {step.due_date_display}</div>
                                      {step.completed_at && (
                                        <div className="text-green-600">
                                          Completed: {format(new Date(step.completed_at), 'dd/MM HH:mm')}
                                        </div>
                                      )}
                                    </div>
                                    <Badge className={stepStatusColors[step.status]}>
                                      {step.status}
                                    </Badge>
                                    {step.is_delayed && (
                                      <Badge variant="destructive">
                                        {step.delay_hours}h delayed
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            
            {filteredData.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                {loading ? 'Loading...' : 'No FMS tasks found'}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
